import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useGlobalStore } from '@/stores';
import {
  CheckEmailResponse,
  LoginCredentials,
  MagicLinkRequest,
  MeResponse,
  MessageResponse,
  ResetPasswordRequest,
  SignUpCredentials,
  TokenResponse,
} from '@/api/auth/auth.types.ts';
import { authApi } from '@/api';

export const useSignIn = (
  options?: UseMutationOptions<TokenResponse, AxiosError, LoginCredentials>,
) => {
  return useMutation<TokenResponse, AxiosError, LoginCredentials>({
    mutationFn: (credentials) =>
      authApi.signIn(credentials).then((res) => res.data),
    ...options,
  });
};

export const useSignUp = (
  options?: UseMutationOptions<TokenResponse, AxiosError, SignUpCredentials>,
) => {
  return useMutation<TokenResponse, AxiosError, SignUpCredentials>({
    mutationFn: (credentials) =>
      authApi.signUp(credentials).then((res) => res.data),
    ...options,
  });
};

export const useCheckEmail = (
  options?: UseMutationOptions<
    CheckEmailResponse,
    AxiosError,
    { email: string }
  >,
) => {
  return useMutation<CheckEmailResponse, AxiosError, { email: string }>({
    mutationFn: ({ email }) =>
      authApi.checkEmail(email).then((res) => res.data),
    ...options,
  });
};

export const useForgotPassword = (
  options?: UseMutationOptions<MessageResponse, AxiosError, { email: string }>,
) => {
  return useMutation<MessageResponse, AxiosError, { email: string }>({
    mutationFn: ({ email }) =>
      authApi.forgotPassword(email).then((res) => res.data),
    ...options,
  });
};

export const useResetPassword = (
  options?: UseMutationOptions<
    MessageResponse,
    AxiosError,
    ResetPasswordRequest
  >,
) => {
  return useMutation<MessageResponse, AxiosError, ResetPasswordRequest>({
    mutationFn: (payload) =>
      authApi.resetPassword(payload).then((res) => res.data),
    ...options,
  });
};

export const useRequestMagicLink = (
  options?: UseMutationOptions<MessageResponse, AxiosError, { email: string }>,
) => {
  return useMutation<MessageResponse, AxiosError, { email: string }>({
    mutationFn: ({ email }) =>
      authApi.requestMagicLink(email).then((res) => res.data),
    ...options,
  });
};

export const useConsumeMagicLink = (
  options?: UseMutationOptions<TokenResponse, AxiosError, MagicLinkRequest>,
) => {
  return useMutation<TokenResponse, AxiosError, MagicLinkRequest>({
    mutationFn: (payload) =>
      authApi.consumeMagicLink(payload).then((res) => res.data),
    ...options,
  });
};

export const useMe = (options?: UseQueryOptions<MeResponse, AxiosError>) => {
  const store = useGlobalStore();
  return useQuery<MeResponse, AxiosError>({
    queryKey: ['me'],
    queryFn: () =>
      authApi.me().then((res) => {
        store.setAccountType(res.data.accountType);
        store.setIsAdmin(res.data.isAdmin);
        store.setPermissions(res.data.permissions);
        store.setBusinessPartnerScope(res.data.businessPartnerScope);
        store.setSalesPersonCode(res.data.salesPersonCode);
        return res.data;
      }),
    ...options,
  });
};
